import * as React from 'react';
import { Card, Grid } from 'ui';
import { ExpandableParagraph, Typography } from 'omakase';
import { centerCropUrl } from '@tock/images';
import { core, useCurrencyContext } from '@tock/coretypes';
import { displayInDollars } from '@tock/money';
import { isEmpty, sortBy, toNumber } from 'es-toolkit/compat';
import {
  menuItemCardContentStyles,
  menuItemCardStyles,
  menuItemContentStyles,
  menuItemContentWithImageStyles,
  menuItemImageStyles,
  menuItemModifierGroupsStyles,
  menuItemModifierItemGroupStyles,
  menuItemNameStyles,
  menuItemRootStyles,
} from 'components/business/menu/menuItemCssDefs';
import { useIntl } from 'react-intl';
import { usePriceDisplayStyle } from 'utils/priceDisplayUtils';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import LazyImage from '@tock/shared/components/LazyImage';

interface MenuItemProps {
  description: string;
  image?: core.IUploadedImage;
  modifiers?: core.ITicketOptionGroup[];
  name: string;
  price: number;
  ticketTypeVariety?: core.TicketType.Variety;
}

const MenuItem: React.FunctionComponent<MenuItemProps> = ({
  description,
  modifiers,
  name,
  price,
  image,
  ticketTypeVariety,
}) => {
  const modifierGroups = !isEmpty(modifiers)
    ? modifiers?.map((modifierGroup) => (
        <div>
          <MenuItemModifierGroup
            modifierGroup={modifierGroup}
            ticketTypeVariety={ticketTypeVariety}
          />
        </div>
      ))
    : null;
  return (
    <Grid item xs={12} md={12} css={menuItemRootStyles} spacing={0}>
      <div css={menuItemCardStyles}>
        <Card variant="outlined">
          <div css={menuItemCardContentStyles}>
            <div css={image ? menuItemContentWithImageStyles : menuItemContentStyles}>
              <Typography css={menuItemNameStyles} component="span" variant="body02Strong" mb={0.5}>
                {name}
              </Typography>
              <ExpandableParagraph
                variant="caption"
                paragraphText={description}
                linkify={true}
                cutoffLength={255}
                mb={1}
              />
              <Typography component="span" variant="body02Strong">
                {price > 0 && <ConsumerDisplayMoney amount={price} />}
              </Typography>
              {!isEmpty(modifiers) && (
                <div css={menuItemModifierGroupsStyles}>{modifierGroups}</div>
              )}
            </div>
            {image && (
              <LazyImage
                alt={image.altText ?? undefined}
                className=""
                css={menuItemImageStyles}
                src={centerCropUrl(image.imageBackingUrl ?? '', {
                  width: 120,
                  height: 120,
                })}
                backgroundColor={image.dominantColor ?? ''}
              />
            )}
          </div>
        </Card>
      </div>
    </Grid>
  );
};

interface MenuItemModifierGroupProps {
  modifierGroup: core.ITicketOptionGroup;
  ticketTypeVariety?: core.TicketType.Variety;
}

const MenuItemModifierGroup: React.FunctionComponent<MenuItemModifierGroupProps> = ({
  modifierGroup,
  ticketTypeVariety,
}) => {
  const useInclusivePricing = usePriceDisplayStyle(ticketTypeVariety) === 'MIN_REQUIRED_SPEND';

  const currency = useCurrencyContext();
  const intl = useIntl();
  const getMenuModifierItemText = (option: core.ITicketOption) => {
    let menuItemText = option.name || '';
    if (option.defaultPrice && option.defaultPrice > 0) {
      const digitsToDisplay = option.defaultPrice % 100 === 0 ? 0 : 2;
      const toDollarsPrice = displayInDollars(option.defaultPrice);

      const formattedPrice = intl.formatNumber(toNumber(toDollarsPrice), {
        currency,
        style: 'currency',
        maximumFractionDigits: digitsToDisplay,
        minimumFractionDigits: digitsToDisplay,
      });
      if (!useInclusivePricing) {
        menuItemText += ` (${formattedPrice})`;
      }
    }
    return menuItemText;
  };

  const modifiers = sortBy(modifierGroup.ticketOption, ['ordering'])
    .map(getMenuModifierItemText)
    .join(', ');
  return (
    <div css={menuItemModifierItemGroupStyles}>
      <Typography variant="captionStrong">{modifierGroup.name}</Typography>
      <ExpandableParagraph variant="caption" paragraphText={modifiers} linkify cutoffLength={128} />
    </div>
  );
};

export default MenuItem;
